import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {FONT_PROXIMA_12, FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_SEMIDARK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
} from 'ui-library/scrollable/ScrollableTableConstants';
import {OnboardingUnitTypeAndUnitsData} from 'units/UnitDictionaryDAO';
import {UnitTypesQueryUnitTypeNode} from 'units/UnitsConstants';

import AddToUnitDictionaryButton from './AddToUnitDictionaryButton';
import {
  getNewSelectedUnitTypesAndUnits,
  getUnitTypeForceSelectedText,
  isUnitRequiredByOtherSelectedUnitTypes,
  UnitsDependencyDataStructures,
} from './OnboardingWizardUtil';

export interface UnitsOnboardingWizardUnitsScreenProps {
  unitTypeData: OnboardingUnitTypeAndUnitsData;
  selectedUnitTypeNames: Array<string>;
  selectedUnitSymbols: Array<string>;
  onSetSelectedUnitTypeNames: (selectedUnitTypeNames: Array<string>) => void;
  onSetSelectedUnitSymbols: (selectedUnitSymbols: Array<string>) => void;
  onSetCurrentUnitTypeName: (currentUnitTypeName: string | null) => void;
  unitsDependencyDataStructures: UnitsDependencyDataStructures;
  existingUnitTypes: Array<UnitTypesQueryUnitTypeNode>;
}

interface OnboardingUnitRowData {
  name: string;
  symbol: string;
  conversionFactor: string;
}

interface OnboardingUnitTableRow extends ScrollableTableRow {
  data: OnboardingUnitRowData;
}

const ONBOARDING_UNITS_TABLE_COLUMNS: Array<ScrollableTableColumn> = [
  {
    header: 'Unit',
    accessorKey: 'name',
    flexGrow: 1,
  },
  {
    header: 'Symbol',
    accessorKey: 'symbol',
  },
  {
    header: 'Conversion factor',
    accessorKey: 'conversionFactor',
  },
];

export default function UnitsOnboardingWizardUnitsScreen({
  unitTypeData,
  selectedUnitTypeNames,
  selectedUnitSymbols,
  onSetSelectedUnitTypeNames,
  onSetSelectedUnitSymbols,
  onSetCurrentUnitTypeName,
  unitsDependencyDataStructures,
  existingUnitTypes,
}: UnitsOnboardingWizardUnitsScreenProps): JSX.Element {
  const {unitType, units, baseUnitSymbol} = unitTypeData;
  const baseUnit = units.find((unit) => unit.symbol === baseUnitSymbol)!;
  const sortedUnits = units.sort((a, b) =>
    a.symbol === baseUnitSymbol ? -1 : b.symbol === baseUnitSymbol ? 1 : 0
  );
  const rows: Array<OnboardingUnitTableRow> = sortedUnits.map((unit) => {
    const unitAlreadyExists = existingUnitTypes.some((existingUnitType) =>
      existingUnitType.units.some((exisitingUnit) => exisitingUnit.symbol === unit.symbol)
    );
    const isUnitRequiredByOtherUnitTypes = isUnitRequiredByOtherSelectedUnitTypes(
      unit.symbol,
      selectedUnitTypeNames,
      unitsDependencyDataStructures
    );
    const forceSelected =
      unitAlreadyExists || unit.symbol === baseUnit.symbol || isUnitRequiredByOtherUnitTypes;
    return {
      id: unit.symbol,
      data: {
        name: unit.name,
        symbol: unit.symbol,
        conversionFactor:
          unit.symbol === baseUnit.symbol ? 'Base unit' : `${unit.conversionFactor} x ${baseUnit.symbol}`,
      },
      // These are required for the global checkbox to work as expected while forcing the base unit to be selected
      displayAsSelected: forceSelected,
      isSelected: selectedUnitSymbols.includes(unit.symbol) && !forceSelected,
      isSelectorDisabled: forceSelected,
    };
  });
  const selectedUnitSymbolsSet = new Set(selectedUnitSymbols);
  const numUnitsSelected = units.filter((unit) => selectedUnitSymbolsSet.has(unit.symbol)).length;
  const numUnitsDetailText = `${numUnitsSelected} of ${units.length} ${pluralize(
    units.length,
    'unit'
  )} selected`;

  const forceSelectedText = getUnitTypeForceSelectedText(
    unitTypeData,
    selectedUnitTypeNames,
    unitsDependencyDataStructures,
    existingUnitTypes
  );

  const handleRowsSelect = (rows: Array<OnboardingUnitTableRow>, isSelected: boolean): void => {
    // Base unit and units required by other unitTypes should already be selected, so don't handle any selection logic here
    const symbolsToHandle = rows
      .map((row) => row.data.symbol)
      .filter((symbol) => {
        const unitAlreadyExists = existingUnitTypes.some((existingUnitType) =>
          existingUnitType.units.some((exisitingUnit) => exisitingUnit.symbol === symbol)
        );
        const isUnitRequiredByOtherUnitTypes = isUnitRequiredByOtherSelectedUnitTypes(
          symbol,
          selectedUnitTypeNames,
          unitsDependencyDataStructures
        );
        const forceSelected =
          symbol === baseUnit.symbol || isUnitRequiredByOtherUnitTypes || unitAlreadyExists;
        return !forceSelected;
      });

    // Auto-select this unitType if user interacts with it
    const {newSelectedUnitTypeNames, newSelectedUnitSymbols} = getNewSelectedUnitTypesAndUnits(
      unitsDependencyDataStructures,
      selectedUnitTypeNames,
      selectedUnitSymbols,
      unitType.name,
      true
    );
    if (isSelected) {
      onSetSelectedUnitSymbols(newSelectedUnitSymbols.concat(symbolsToHandle));
    } else {
      onSetSelectedUnitSymbols(newSelectedUnitSymbols.filter((symbol) => !symbolsToHandle.includes(symbol)));
    }
    onSetSelectedUnitTypeNames(newSelectedUnitTypeNames);
  };

  return (
    <div {...getTestProps({component: 'UnitsOnboardingWizardUnitsScreen', element: 'root'})}>
      <div className={css(styles.header)}>
        <div className={css(styles.leftHeaderPortion)}>
          <BenchlingIcon
            icon="chevron-left"
            onClick={() => {
              onSetCurrentUnitTypeName(null);
            }}
            className={css(styles.chevron)}
          />
          <div className={css(styles.textContainer)}>
            <span className={css(styles.unitTypeName)}>{unitType.name}</span>
            <span className={css(styles.numUnitsDetailText)}>{numUnitsDetailText}</span>
          </div>
        </div>
        <div className={css(styles.rightHeaderPortion)}>
          <AddToUnitDictionaryButton
            added={selectedUnitTypeNames.includes(unitType.name)}
            onSetAdded={(selected: boolean): void => {
              const {newSelectedUnitTypeNames, newSelectedUnitSymbols} = getNewSelectedUnitTypesAndUnits(
                unitsDependencyDataStructures,
                selectedUnitTypeNames,
                selectedUnitSymbols,
                unitType.name,
                selected
              );
              onSetSelectedUnitTypeNames(newSelectedUnitTypeNames);
              onSetSelectedUnitSymbols(newSelectedUnitSymbols);
            }}
            forceSelectedText={forceSelectedText}
          />
        </div>
      </div>
      <ScrollableTable
        className={css(styles.table)}
        columns={ONBOARDING_UNITS_TABLE_COLUMNS}
        rows={rows}
        onRowsSelect={handleRowsSelect}
        onRowClick={(row: OnboardingUnitTableRow): void => {
          const isSelected = !selectedUnitSymbols.includes(row.data.symbol);
          handleRowsSelect([row], isSelected);
        }}
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        canResizeColumns={true}
        tableKey={TABLE_KEYS.UNITS_ONBOARDING_WIZARD_TABLE}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  table: {
    height: '100%',
  },
  chevron: {
    width: 16,
    height: 16,
    marginLeft: 12,
    marginRight: 12,
    cursor: 'pointer',
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 12,
  },
  unitTypeName: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  numUnitsDetailText: {
    ...FONT_PROXIMA_12,
    color: GRAY_SEMIDARK,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    height: 65,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftHeaderPortion: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightHeaderPortion: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
});
